/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { OperationStatus } from "Constants/OperationStatus";
import { AppState } from "Dataflows/Reducers";
import { IProfileBenefitResult } from "Interfaces/Benefits/IProfileBenefitResult";
import { IFilterValue } from "Interfaces/IFilterValue";
import { IOperationStatusType } from "Interfaces/IOperationStatusType";
import { IProgramProfile } from "Interfaces/ProgramProfile/IProgramProfile";
import { IDistributionList } from "Interfaces/UserProfile/DistributionLists/IDistributionLists";
import { IUserProfile } from "Interfaces/UserProfile/IUserProfile";
import { IUserProfileComment } from "Interfaces/UserProfile/IUserProfileComment";
import { IUserProfileVSBenefit } from "Interfaces/UserProfile/IUserProfileVSBenefit";

import { IUserProfileState } from "./IUserProfileState";

/**
 * Returns current value from state for: userProfileId
 * @param userProfileState
 */
export const getUserProfileId = (userProfileState: IUserProfileState): number =>
  userProfileState.userProfileId;

/**
 * Returns current value from state for: userProfiles
 * @param userProfileState
 */
export const getUserProfiles = (userProfileState: IUserProfileState): IUserProfile[] =>
  userProfileState.userProfiles;

/**
 * Returns current value from state for: userProfiles
 * @param userProfileState
 */
export const getUserProfilesFilterValues = (userProfileState: IUserProfileState): IFilterValue[] =>
  userProfileState.userProfileFilterValues;

/**
 * Returns current value from state for: selectedUserProfile
 * @param userProfileState
 */
export const getUserProfile = (userProfileState: IUserProfileState): IUserProfile =>
  userProfileState.selectedUserProfile;

export const getAppStateUserProfile = (state: AppState): IUserProfile =>
  state.userProfileState.selectedUserProfile;

/**
 * Returns current value from state for: isUserProfilesLoading
 * @param userProfileState
 */
export const isUserProfilesLoading = (userProfileState: IUserProfileState): boolean =>
  userProfileState.isUserProfilesLoading;

/**
 * Returns current value from state for: isSelectedUserProfileUpdating
 * @param userProfileState
 */
export const isSelectedUserProfileUpdating = (userProfileState: IUserProfileState): boolean =>
  userProfileState.isSelectedUserProfileUpdating;

export const isAppStateSelectedUserProfileUpdating = (state: AppState): boolean =>
  state.userProfileState.isSelectedUserProfileUpdating;

/**
 * Returns current value from state for: isCurrentUserProfileLoading
 * @param userProfileState
 */
export const isCurrentUserProfileLoading = (userProfileState: IUserProfileState): boolean =>
  userProfileState.isCurrentUserProfileLoading;

export const isAppStateCurrentUserProfileLoading = (state: AppState): boolean =>
  state.userProfileState.isCurrentUserProfileLoading;

/**
 * Returns current value from state for: error
 * @param userProfileState
 */
export const getLoadingError = (userProfileState: IUserProfileState): string =>
  userProfileState.error;

/**
 * Returns current value from state for: updateUserProfileError
 * @param userProfileState
 */
export const getUpdateUserProfileError = (userProfileState: IUserProfileState): string =>
  userProfileState.updateUserProfileError;

export const getAppStateUpdateUserProfileError = (state: AppState): string =>
  state.userProfileState.updateUserProfileError;

// COMMENTS
/**
 * Returns current value from state for: userProfileComments
 * @param userProfileState
 */
export const getUserProfileComments = (
  userProfileState: IUserProfileState
): IUserProfileComment[] | undefined => userProfileState.commentsState.userProfileComments;

export const getAppStateUserProfileComments = (
  state: AppState
): IUserProfileComment[] | undefined => state.userProfileState.commentsState.userProfileComments;

/**
 * Returns current value from state for: isUserProfileCommentsLoading
 * @param userProfileState
 */
export const isUserProfileCommentsLoading = (userProfileState: IUserProfileState): boolean =>
  userProfileState.isUserProfileCommentsLoading;

/**
 * Returns current value from state for: loadCommentsError
 * @param userProfileState
 */
export const getLoadCommentsError = (state: AppState): string | undefined =>
  state.userProfileState.commentsState?.commentsErrorMessage;

// VSBENEFITS
/**
 * Returns current value from state for: userProfileVSBenefit
 * @param userProfileState
 */
export const getUserProfileVSBenefit = (
  userProfileState: IUserProfileState
): IUserProfileVSBenefit | undefined => userProfileState.userProfileVSBenefit;

export const getAppStateUserProfileVSBenefit = (
  state: AppState
): IUserProfileVSBenefit | undefined => state.userProfileState.userProfileVSBenefit;

/**
 * Returns current value from state for: isLoadingVSBenefits
 * @param userProfileState
 */
export const isLoadingVSBenefits = (userProfileState: IUserProfileState): boolean =>
  userProfileState.isLoadingVSBenefits;

/**
 * Returns current value from state for: loadVSBenefitsError
 * @param userProfileState
 */
export const getVSBenefitsFetchErrorMessage = (userProfileState: IUserProfileState): string =>
  userProfileState.loadVSBenefitsError;

/**
 * Returns current value from state for: isUpdatingVSBenefits
 * @param userProfileState
 */
export const isUpdatingVSBenefits = (userProfileState: IUserProfileState): boolean =>
  userProfileState.isUpdatingVSBenefits;

/**
 * Returns current value from state for: updateVSBenefitsError
 * @param userProfileState
 */
export const getVSBenefitsUpdateErrorMessage = (userProfileState: IUserProfileState): string =>
  userProfileState.updateVSBenefitsError;

// LISTING SEARCH AND PAGING
/**
 * Returns current value from state for: currentPage
 * @param userProfileState
 */
export const getCurrentPage = (userProfileState: IUserProfileState): number =>
  userProfileState.currentPage;

/**
 * Returns current value from state for: pageSize
 * @param userProfileState
 */
export const getPageSize = (userProfileState: IUserProfileState): number =>
  userProfileState.pageSize;

/**
 * Returns current value from state for: searchText
 * @param userProfileState
 */
export const getSearchText = (userProfileState: IUserProfileState): string =>
  userProfileState.searchText;

/**
 * Returns the programProfileUpdateError message.
 * @param userProfileState
 */
export const getProgramProfileUpdateErrorMessage = (userProfileState: IUserProfileState): string =>
  userProfileState.programProfileUpdateError;

export const getProgramProfileOperationStatus = (state: AppState): IOperationStatusType =>
  state.userProfileState.programProfileOperationStatus;

export const getProgramProfileErrorMessage = (state: AppState): string =>
  state.userProfileState.fetchProgramProfileError;

/**
 * Returns the filtered count.
 * @param userProfileState
 */
export const getFilteredCount = (userProfileState: IUserProfileState): number =>
  userProfileState.filteredCount;

/**
 * Returns the isClaimingBenefit value.
 * @param {AppState} state
 * @returns {boolean}
 */
export const getIsClaimingBenefit = (state: AppState): boolean =>
  state.userProfileState.isClaimingBenefit;

/**
 * Returns the isClaimingBenefitSuccess value.
 * @param {AppState} state
 * @returns {boolean}
 */
export const getIsClaimingBenefitSuccess = (state: AppState): boolean =>
  state.userProfileState.isClaimingBenefitSuccess;

/**
 * Returns the isClaimingBenefitError value.
 * @param {AppState} state
 * @returns {string}
 */
export const getIsClaimingBenefitsError = (state: AppState): string =>
  state.userProfileState.benefitsOperationStatus?.create === OperationStatus.Failed &&
  !!state.userProfileState.claimBenefitError
    ? state.userProfileState.claimBenefitError
    : "";

/**
 * Returns the getClaimingBenefitError value.
 * @param {AppState} state
 * @returns {boolean}
 */
export const getClaimingBenefitError = (state: AppState): string =>
  state.userProfileState.claimBenefitError;

/**
 * Returns the getProfileBenefits value.
 * @param {AppState} state
 * @returns {IProfileBenefitResult}
 */
export const getProfileBenefits = (state: AppState): IProfileBenefitResult =>
  state.userProfileState.profileBenefits;

/**
 * Returns the getProfileBenefitsStatus value.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const getProfileBenefitsStatus = (state: AppState): OperationStatus =>
  state.userProfileState.profileBenefitsStatus;

/**
 * Returns the getProfileBenefitsErrorMessage value.
 * @param {AppState} state
 * @returns {string}
 */
export const getProfileBenefitsErrorMessage = (state: AppState): string =>
  state.userProfileState.profileBenefitsErrorMessage;

/**
 * Returns the getUpdateVSProfileBenefitsStatus value.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const getUpdateVSProfileBenefitStatus = (state: AppState): OperationStatus =>
  state.userProfileState.updatingVSBenefitOperationStatus;

/**
 * Returns the getProfileBenefitsErrorMessage value.
 * @param {AppState} state
 * @returns {string}
 */
export const getUpdateVSProfileBenefitsError = (state: AppState): string =>
  state.userProfileState.updateVSBenefitsError;

/**
 * Returns program profile.
 * @param appState
 */
export const getProgramProfile = (state: AppState): IProgramProfile | undefined =>
  state.userProfileState.selectedUserProfile.programProfile;

/**
 * Returns loading status of user profile comments.
 * @param appState
 * @returns {boolean}
 */
export const getAddCommentOperationStatus = (state: AppState): OperationStatus | undefined =>
  state.userProfileState.commentsState.commentsOperationStatus?.create;

/**
 * Returns operation status of update blocked shipping.
 * @param appState
 * @returns {string}
 */
export const getUserProfileUpdateBlockShippingStatus = (
  state: AppState
): OperationStatus | undefined => state.userProfileState.updateBlockShippingOperationStatus;

/**
 * Returns operation status of update blocked shipping.
 * @param appState
 * @returns {string}
 */
export const getUserProfileUpdateBlockShippingErrorMessage = (state: AppState): string =>
  state.userProfileState.updateBlockShippingErrorMessage;

/**
 * Returns distribution list details.
 * @param appState
 * @returns {IDistributionList[]}
 */
export const getUserProfileDl = (state: AppState): IDistributionList[] =>
  state.userProfileState.distributionLists;

/**
 * Returns distribution list details operation status.
 * @param appState
 * @returns {OperationStatus}
 */
export const getUserProfileDlLoadStatus = (state: AppState): OperationStatus =>
  state.userProfileState.loadingDlStatus;

/**
 * Returns distribution list remove operation status.
 * @param appState
 * @returns {OperationStatus}
 */
export const getUserProfileDlRemoveStatus = (state: AppState): OperationStatus =>
  state.userProfileState.removingDlStatus;

/**
 * Returns distribution list remove error.
 * @param appState
 * @returns {string}
 */
export const getUserProfileDlRemoveError = (state: AppState): string =>
  state.userProfileState.removingDlErrorMessage;

/**
 * Returns distribution list remove all operation status.
 * @param appState
 * @returns {OperationStatus}
 */
export const getUserProfileDlRemoveAllStatus = (state: AppState): OperationStatus =>
  state.userProfileState.removingAllDlStatus;

/**
 * Returns distribution list remove all error.
 * @param appState
 * @returns {string}
 */
export const getUserProfileDlRemoveAllError = (state: AppState): string =>
  state.userProfileState.removingAllErrorMessage;

/**
 * Returns update user profile is private error.
 * @param appState
 * @returns {string}
 */
export const getUserProfileUpdateIsPrivateError = (state: AppState): string =>
  state.userProfileState.updateIsPrivateProfileError;

/**
 * Returns update user profile is private operation status.
 * @param appState
 * @returns {OperationStatus}
 */
export const getUserProfileUpdateIsPrivateOperationStatus = (state: AppState): OperationStatus =>
  state.userProfileState.updateIsPrivateProfileStatus;

/**
 * Returns the isClaimingBenefitSuccess value.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const getIsClaimingBenefitStatus = (state: AppState): OperationStatus | undefined =>
  state.userProfileState.benefitsOperationStatus?.create;
